import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    Avatar,
    AvatarGroup,
    AvatarGroupItem,
    AvatarGroupPopover,
    partitionAvatarGroupItems,
    Tooltip,
} from "@fluentui/react-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    getRunbookDetailRequest,
    startRunbookRequest,
    stopRunbookRequest,
    createRunbookActionRequest,
    updateRunbookActionRequest,
    deleteRunbookActionRequest,
    completeRunbookActionRequest,
    duplicateRunbookActionRequest,
    reorderRunbookActionRequest,
} from "../../../store/actions/runbooks.actions";
import {
    runbookDetail,
    runbookDetailLoading,
    runbookLoading,
} from "../../../store/selectors/runbooks.selector";
import {
    RunbookStatus,
    RunbookActionStatus,
    getRunbookStatusLabel,
    getRunbookStatusClass,
    getRunbookActionStatusLabel,
    getRunbookActionStatusClass,
    formatDuration,
    formatElapsed,
    UserSearchRefType,
    exportFormats,
    getLabelByType,
} from "../../../helpers/utils";
import { generalActions } from "../../../store/actions";
import RunbookModal from "../RunbookModal";
import RunbookActionModal from "../RunbookActionModal";
import UserSelect from "../../../components/userSelect/UserSelect";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import styles from "./RunbookOverview.module.scss";
import { Check, Copy, Download, Pencil, Trash, X } from "lucide-react";
import { utcToLocalDateTimeString, utcToLocalTimeString } from "../../../utils/systemSettings";
import HoverCard from "../../../components/hoverCard/HoverCard";
import Modal from "../../../components/controls/modal/Modal";
import runbooksService from "../../../store/services/runbooks.service";
import Input from "../../../components/controls/input/customInput/CustomInput";
import { AttachementIcon, Cross, FileDownSvg, PrintIcon } from "../../../modules/default-svg";
import { Spinner } from "react-bootstrap";
import Button from "../../../components/controls/button";
import CustomPopover from "../../../components/popover/Popover";
import CustomButton from "../../../components/controls/button/Button";
import DropDown from "../../../components/controls/dropdown/DropdownFluent";
import runbookService from "../../../store/services/runbooks.service";

// ─── Helpers ────────────────────────────────────────────────────────────────
const settings = JSON.parse(localStorage.getItem("settings"));
const formatDateTimeLabel = (date) => {
    if (!date)
        return '--';

    const dateFormat = settings?.regionalSettings?.dateFormat ? settings?.regionalSettings?.dateFormat.replace(/dd/g, 'DD').replace(/yy/g, 'YY').replace(/mm/g, 'MM') : 'YY-MM-DD';
    const timeFormat = settings?.regionalSettings?.timeFormat ? (settings?.regionalSettings?.timeFormat.trim().toLowerCase() === "hh:mm a" ? "hh:mm a" : "HH:mm") : "HH:mm";

    return <p><b className={"mr-2"}>{moment.utc(date).local().format(`${dateFormat}`)}</b>{moment.utc(date).local().format(`${timeFormat}`)}</p>;
}
const formatTimeLabel = (iso) => iso ? utcToLocalTimeString(settings, iso) : "--";
const getAssignees = (assignees) => {
    const { inlineItems, overflowItems } = partitionAvatarGroupItems({
        items: assignees,
    });
    return (
        <AvatarGroup layout="stack" className="listingAvatarGroup" size={20}>
            {inlineItems.map((item) => (
                <HoverCard
                    HoverCardTrigger={
                        <Avatar
                            color="colorful"
                            size={20}
                            name={item.name}
                            image={{ src: item.image }}
                            key={item.name}
                        />
                    }

                    AvatarId={item.id}
                    AvatarName={item.name}
                    AvatarImage={item.image}
                    AvatarDesignation={item.title || ""}
                    AvatarEmail={item.email}
                    AvatarPhone={item.phoneNumber}
                    AvatarIsExternal={item.isExternal}
                />
            ))}
            {overflowItems && (
                <AvatarGroupPopover>
                    {overflowItems.map((item) => (
                        <div className="d-flex inline-items gap-2">
                            <HoverCard
                                HoverCardTrigger={
                                    <Avatar
                                        color="colorful"
                                        size={20}
                                        name={item.name}
                                        image={{ src: item.image }}
                                        key={item.name}
                                    />
                                }
                                AvatarId={item.id}
                                AvatarName={item.name}
                                AvatarImage={item.image}
                                AvatarDesignation={item.title || ""}
                                AvatarEmail={item.email}
                                AvatarPhone={item.phoneNumber}
                                AvatarIsExternal={item.isExternal}
                            />
                            <p className="font-figtree text-[13px]">{item.name}</p>
                        </div>
                    ))}
                </AvatarGroupPopover>
            )}
        </AvatarGroup>
    );
};
// ─── RunbookHeader ───────────────────────────────────────────────────────────

const RunbookHeader = ({ runbook, projectId, onSave, onStart, onStop, saving }) => {
    const permissions = localStorage.getItem("permissions");
    const canEdit = permissions?.includes("Runbook.Edit");
    const [editOpen, setEditOpen] = useState(false);
    const [confirmationModalData, setConfirmationModalData] = useState(null);
    const { status, actualStartDate } = runbook;
    const [elapsed, setElapsed] = useState(0);
    const intervalRef = useRef(null);

    const variance = runbook.status === RunbookStatus.Completed || runbook.status === RunbookStatus.Aborted
        ? runbook.actualDurationMinutes - runbook.plannedDurationMinutes
        : null;

    useEffect(() => {
        if (actualStartDate) {
            const base = Date.now() - new Date(actualStartDate).getTime();
            setElapsed(base > 0 ? base : 0);
        } else {
            setElapsed(0);
        }
    }, [actualStartDate, status]);

    useEffect(() => {
        if (status === RunbookStatus.InProgress) {
            intervalRef.current = setInterval(() => setElapsed((p) => p + 1000), 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [status]);

    const isDone = status === RunbookStatus.Completed || status === RunbookStatus.Aborted;

    const handleStart = () => {
        setConfirmationModalData({
            title: "Start Runbook",
            body: "Are you sure you want to start this runbook?",
            saveBtnLabel: "Start",
            cancelBtnLabel: "Cancel",
            onConfirm: () => {
                onStart();
            },
        });
    };
    const handleStop = () => {
        setConfirmationModalData({
            title: "Stop Runbook",
            body: "Are you sure you want to stop this runbook?",
            saveBtnLabel: "Stop",
            cancelBtnLabel: "Cancel",
            onConfirm: () => {
                onStop();
            },
        });
    };
    return (
        <div className={styles.headerCard}>
            <div className={styles.titleRow}>
                {/* Left: identity */}
                <div>
                    <div className={styles.badgeRow}>
                        <Tooltip content={"Status"} relationship="label">
                            <span className={`${styles.runbookStatusBadge} ${getRunbookStatusClass(runbook.status)}`}>
                                {getRunbookStatusLabel(runbook.status)}
                            </span>
                        </Tooltip>
                        {
                            (runbook.refId && runbook.refId !== "") && (
                                <Tooltip content={"Change Ticket"} relationship="label">
                                    <span className={`${styles.runbookStatusBadge} pill runbook-ref-id`}>
                                        {runbook.refId}
                                    </span>
                                </Tooltip>
                            )
                        }

                        <h4 className={styles.runbookTitle}>{runbook.name}</h4>
                    </div>
                </div>

                {/* Right: execution controls + edit */}
                <div className={styles.execControls}>
                    {!isDone && status === RunbookStatus.InProgress && (
                        <div className={styles.elapsed}>{formatElapsed(elapsed)}</div>
                    )}
                    {!isDone && (status === RunbookStatus.Draft || status === RunbookStatus.Ready) && canEdit && (
                        <button className={styles.btnStart} disabled={saving} onClick={handleStart}>
                            ▶ Start Runbook
                        </button>
                    )}
                    {!isDone && status === RunbookStatus.InProgress && canEdit && (
                        <button className={styles.btnStop} disabled={saving} onClick={handleStop}>
                            ⏹ Stop
                        </button>
                    )}
                    {canEdit && (
                        <button className={styles.editBtn} onClick={() => setEditOpen(true)}>
                            Edit
                        </button>
                    )}
                </div>
            </div>

            {/* Dates */}
            <div className={styles.dateMeta}>
                <div className={styles.dateBlocks}>
                    {runbook.owner && (
                        <div className={styles.blockAssignees}>
                            <span className={styles.blockAssigneesLabel}>Owner</span>
                            <span className={styles.blockAssignees}>
                                <div className="d-flex inline-items gap-2">
                                    <HoverCard
                                        HoverCardTrigger={
                                            <Avatar
                                                color="colorful"
                                                size={20}
                                                name={runbook.owner.name}
                                                image={{ src: runbook.owner.image }}
                                                key={runbook.owner.name}
                                            />
                                        }
                                        AvatarId={runbook.owner.id}
                                        AvatarName={runbook.owner.name}
                                        AvatarImage={runbook.owner.image}
                                        AvatarDesignation={runbook.owner.title || ""}
                                        AvatarEmail={runbook.owner.email}
                                        AvatarPhone={runbook.owner.phoneNumber}
                                        AvatarIsExternal={runbook.owner.isExternal}
                                    />
                                    <p className="font-figtree text-[13px]">{runbook.owner.name}</p>
                                </div>
                            </span>
                        </div>
                    )}
                    {/* Planned */}
                    {(runbook.plannedStartDate || runbook.plannedEndDate) && (
                        <div className={styles.dateBlockPlanned}>
                            <span className={styles.dateBlockLabel}>Planned</span>
                            <span className={styles.datePillPlanned}>{formatDateTimeLabel(runbook.plannedStartDate)}</span>
                            <span className={styles.dateSep}>→</span>
                            <span className={styles.datePillPlanned}>{formatTimeLabel(runbook.plannedEndDate)}</span>
                            {(runbook.plannedStartDate && runbook.plannedEndDate) && (
                                <span className={`${styles.dateDurationPlanned} ml-2`}>{formatDuration(runbook.plannedDurationMinutes)}</span>
                            )}
                        </div>
                    )}

                    {/* Actual */}
                    {(runbook.actualStartDate || runbook.actualEndDate) && (
                        <div className={styles.dateBlockActual}>
                            <span className={styles.dateBlockLabel}>Actual</span>
                            <span className={styles.datePillActual}>{formatDateTimeLabel(runbook.actualStartDate)}</span>
                            <span className={styles.dateSep}>→</span>
                            <span className={styles.datePillActual}>{formatTimeLabel(runbook.actualEndDate)}</span>
                            {(runbook.actualStartDate && runbook.actualEndDate) && (
                                <span className={`${styles.dateDurationActual} ml-2`}>{formatDuration(runbook.actualDurationMinutes)}</span>
                            )}
                        </div>
                    )}

                    {/* Variance */}
                    {variance !== null && (
                        <div className={styles.dateBlockVariance}>
                            <span className={styles.dateBlockLabel}>Variance</span>
                            <span className={variance <= 0 ? styles.varianceNegative : styles.variancePositive}>
                                {variance <= 0 ? `-${formatDuration(Math.abs(variance))}` : `+${formatDuration(variance)}`}
                            </span>
                        </div>
                    )}
                </div>
                <div className={styles.metaRow}>
                    {runbook.assignees && runbook.assignees.length > 0 && (
                        <div className={styles.blockAssignees}>
                            <span className={styles.blockAssigneesLabel}>Assignees</span>
                            <span className={styles.blockAssignees}>
                                {getAssignees(runbook.assignees)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {editOpen && (
                <RunbookModal
                    id={runbook.id}
                    projectId={projectId}
                    onSave={onSave}
                    onClose={() => setEditOpen(false)}
                />
            )}
            {/* Confirmation */}
            {confirmationModalData && (
                <Modal
                    title={confirmationModalData.title}
                    body={confirmationModalData.body}
                    handleCancel={() => setConfirmationModalData(null)}
                    handleSave={() => {
                        if (confirmationModalData?.onConfirm)
                            confirmationModalData.onConfirm();

                        setConfirmationModalData(null);
                    }
                    }
                    saveBtnLabel={confirmationModalData.saveBtnLabel}
                    cancelBtnLabel={confirmationModalData.cancelBtnLabel}
                    savebtnLabel={confirmationModalData.confirmationBtn}
                    isLoading={false}
                />
            )}
        </div>
    );
};

// ─── RunbookActionRow ────────────────────────────────────────────────────────

const RunbookActionRow = ({
    action,
    runbookId,
    projectId,
    isLocked,
    onComplete,
    onDelete,
    onSave,
    onDuplicate,
    attributes,
    listeners,
    style,
    isDragging,
}) => {
    const permissions = localStorage.getItem("permissions");
    const canEdit = permissions?.includes("Runbook.Edit");
    const canCreate = permissions?.includes("Runbook.Create");
    const [editOpen, setEditOpen] = useState(false);
    const [confirmationModalData, setConfirmationModalData] = useState(null);

    const statusColors = {
        [RunbookActionStatus.Pending]: "#9e9e9e",
        [RunbookActionStatus.InProgress]: "#5aa55a",
        [RunbookActionStatus.Completed]: "#0f6cbd",
        [RunbookActionStatus.Failed]: "#d1424f",
    };

    const handleComplete = () => {
        setConfirmationModalData({
            title: "Complete Action",
            body: `Are you sure you want to mark ${action.title} as completed?`,
            saveBtnLabel: "Complete",
            cancelBtnLabel: "Cancel",
            onConfirm: () => {
                onComplete(action);
            },
        });
    };

    const handleDelete = () => {
        setConfirmationModalData({
            title: "Delete Action",
            body: `Are you sure you want to delete ${action.title}?`,
            saveBtnLabel: "Delete",
            cancelBtnLabel: "Cancel",
            onConfirm: () => {
                onDelete(action);
            },
        });
    };

    return (
        <>
            <div
                className={`${styles.actionRow} ${isDragging ? styles.actionRowDragging : ""}`}
                style={style}
            >
                {/* Drag handle */}
                {/* {!isLocked && canEdit && (
                    <span
                        {...attributes}
                        {...listeners}
                        className={styles.dragHandle}
                        title="Drag to reorder"
                    >
                        ⠿
                    </span>
                )} */}

                {/* Step number circle */}
                <Tooltip content={getRunbookActionStatusLabel(action.status)} relationship="label">
                    <div
                        className={`${styles.stepCircle}`}
                        style={{ background: statusColors[action.status] ?? "#9e9e9e" }}>
                        {action.order + 1}
                    </div>
                </Tooltip>

                {/* Title */}

                <span className={styles.actionTitle}>
                    {(action.attachments?.length > 0) && (
                        <div className="flex mr-1">
                            <AttachementIcon count={action.attachments?.length} />
                        </div>
                    )}
                    {action.title}
                </span>


                {/* Assignees */}
                {action.assignees && action.assignees.length > 0 && (
                    <div className={styles.blockAssignees}>
                        <span className={styles.blockAssigneesLabel}> </span>
                        <span className={styles.blockAssignees}>
                            {getAssignees(action.assignees)}
                        </span>
                    </div>
                )}
                {/* Planned dates + duration */}
                {
                    (action.plannedStartDate || action.plannedEndDate) && (
                        <Tooltip content={"Planned"} relationship="label">
                            <div className={`${styles.dateBlockPlanned} ${styles.dateBlockMinWidth}`}>
                                <span className={`${styles.datePillPlanned} font-bold`}>{formatTimeLabel(action.plannedStartDate)}</span>
                                <span className={styles.dateSep}>→</span>
                                <span className={`${styles.datePillPlanned} font-bold`}>{formatTimeLabel(action.plannedEndDate)}</span>
                                <span className={`${styles.dateDurationPlanned} ${styles.dateDurationMinWidth}`}>{formatDuration(action.plannedDurationMinutes)}</span>
                            </div>
                        </Tooltip>
                    )}

                {/* Actual duration */}
                {(action.actualStartDate || action.actualEndDate) && (
                    <Tooltip content={"Actual"} relationship="label">
                        <div className={`${styles.dateBlockActual} ${styles.dateBlockMinWidth}`}>
                            <span className={`${styles.datePillActual} font-bold`}>{formatTimeLabel(action.actualStartDate)}</span>
                            <span className={styles.dateSep}>→</span>
                            <span className={`${styles.datePillActual} font-bold`}>{formatTimeLabel(action.actualEndDate)}</span>
                            <span className={`${styles.dateDurationActual} ${styles.dateDurationMinWidth}`}>{formatDuration(action.actualDurationMinutes)}</span>
                        </div>
                    </Tooltip>
                )}

                {/* Action buttons */}
                <div className={styles.actionBtns}>
                    {action.status === RunbookActionStatus.InProgress && canEdit && (
                        <Tooltip content="Mark Complete" relationship="label">
                            <button className={styles.btnComplete} onClick={handleComplete}>
                                <Check size={16} />
                            </button>
                        </Tooltip>
                    )}
                    {canEdit && (
                        <Tooltip content="Duplicate" relationship="label">
                            <span style={{ cursor: "pointer", color: "#342c82" }} onClick={() => onDuplicate(action)}>
                                <Copy size={16} />
                            </span>
                        </Tooltip>
                    )}
                    {canEdit && (
                        <Tooltip content="Edit" relationship="label">
                            <span style={{ cursor: "pointer", color: "#342c82" }} onClick={() => setEditOpen(true)}>
                                <Pencil size={16} />
                            </span>
                        </Tooltip>
                    )}
                    {action.status !== RunbookActionStatus.InProgress && permissions?.includes("Runbook.Delete") && (
                        <Tooltip content="Delete" relationship="label">
                            <span style={{ cursor: "pointer", color: "#d32f2f" }} onClick={handleDelete}>
                                <Trash size={16} />
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>

            {editOpen && (
                <RunbookActionModal
                    id={action?.id}
                    projectId={projectId}
                    runbookId={runbookId}
                    onSave={() => { }}
                    onClose={() => setEditOpen(false)}
                />
            )}
            {/* Confirmation */}
            {confirmationModalData && (
                <Modal
                    title={confirmationModalData.title}
                    body={confirmationModalData.body}
                    handleCancel={() => setConfirmationModalData(null)}
                    handleSave={() => {
                        if (confirmationModalData?.onConfirm)
                            confirmationModalData.onConfirm();

                        setConfirmationModalData(null);
                    }
                    }
                    saveBtnLabel={confirmationModalData.saveBtnLabel}
                    cancelBtnLabel={confirmationModalData.cancelBtnLabel}
                    savebtnLabel={confirmationModalData.confirmationBtn}
                    isLoading={false}
                />
            )}
        </>
    );
};

// ─── SortableActionRow ───────────────────────────────────────────────────────

const SortableActionRow = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.action.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef}>
            <RunbookActionRow
                {...props}
                attributes={attributes}
                listeners={listeners}
                style={style}
                isDragging={isDragging}
            />
        </div>
    );
};

// ─── RunbookActionInlineForm ──────────────────────────────────────────────────

const RunbookActionInlineForm = ({ runbook, projectId, existingActions, onSuccess }) => {
    const dispatch = useDispatch();
    const saving = useSelector(runbookLoading);
    const [title, setTitle] = useState("");
    const [plannedStart, setPlannedStart] = useState(null);
    const [plannedEnd, setPlannedEnd] = useState(null);
    const [assignees, setAssignees] = useState([]);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        const lastAction = existingActions.reduce(
            (max, action) => !max || action.order > max.order ? action : max,
            null
        );

        if (lastAction || runbook.plannedStartDate) {
            setPlannedStart(lastAction ? lastAction.plannedEndDate : runbook.plannedStartDate);
            setPlannedEnd(lastAction ? lastAction.plannedEndDate : runbook.plannedStartDate);
        }
    }, [existingActions, runbook]);

    const handleAdd = () => {
        if (!title.trim()) {
            setError("Action title is required");
            return;
        }

        setError("");

        dispatch(
            createRunbookActionRequest({
                runbookId: runbook.id,
                payload: {
                    title: title.trim(),
                    plannedStartDate: plannedStart,
                    plannedEndDate: plannedEnd,
                    assigneeIds: assignees ? assignees.map((a) => a.id) : [],
                },
                onSuccess: () => {
                    setTitle("");
                    setPlannedStart(null);
                    setPlannedEnd(null);
                    setAssignees([]);
                    onSuccess && onSuccess();
                    inputRef?.current?.focus();
                },
            })
        );
    };

    return (
        <div className={styles.inlineForm}>
            <div className="row gx-2 flex-nowrap w-100" style={{ flex: "1 1 auto" }}>
                <div className="col">
                    <label className={` Input_label w-100`}>
                        {"Title"} {<span className="Input_staric">*</span>}
                        {error && <span className="text-danger w-100" style={{ fontSize: 12 }}>{error}</span>}
                    </label>
                    <input
                        ref={inputRef}
                        className={`${styles.inlineInput} ${error ? styles.error : ""}`}
                        placeholder="Action title"
                        value={title}
                        maxLength={255}
                        onChange={(e) => { setTitle(e.target.value); setError(""); }}
                    />

                </div>
                <div className="col-auto">
                    <Input
                        label="Planned Start"
                        value={plannedStart}
                        type="datetime"
                        handleChange={(v) => setPlannedStart(v)}
                        maxDate={plannedEnd}
                    />
                </div>
                <div className="col-auto">
                    <Input
                        label="Planned End"
                        value={plannedEnd}
                        type="datetime"
                        handleChange={(v) => setPlannedEnd(v)}
                        minDate={plannedStart}
                    />
                </div>
                <div className="col-auto">
                    <UserSelect
                        label="Assignees"
                        buttonLabel={"Select Assignees"}
                        multiple={true}
                        refType={UserSearchRefType.Project}
                        refId={projectId}
                        value={assignees}
                        onChange={(selected) => { setAssignees(selected || []); }}
                        selectedUsersDisplayMode="avatar-only"
                        selectedUsersDisplayLimit={3}
                    />
                </div>
                <div className="col-auto ms-auto align-self-center mt-3">
                    <button className={styles.btnAdd} disabled={saving} onClick={handleAdd}>
                        + Add
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── RunbookActionsList ───────────────────────────────────────────────────────

const ACTION_FILTER_OPTIONS = [
    { label: "All", value: null, color: "#342c82" },
    { label: getRunbookActionStatusLabel(RunbookActionStatus.Pending), value: RunbookActionStatus.Pending, color: "#9e9e9e" },
    { label: getRunbookActionStatusLabel(RunbookActionStatus.InProgress), value: RunbookActionStatus.InProgress, color: "#5aa55a" },
    { label: getRunbookActionStatusLabel(RunbookActionStatus.Completed), value: RunbookActionStatus.Completed, color: "#0f6cbd" },
    { label: getRunbookActionStatusLabel(RunbookActionStatus.Failed), value: RunbookActionStatus.Failed, color: "#d1424f" },
];

const NON_ALL_STATUS_COUNT = ACTION_FILTER_OPTIONS.filter((o) => o.value !== null).length;

const RunbookActionsList = ({ runbook, projectId, onRefresh }) => {
    const dispatch = useDispatch();
    const [actions, setActions] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilters, setStatusFilters] = useState(new Set());
    const [openPopover, setIsOpenPopover] = useState(false);
    const [exportFormat, setExportFormat] = useState(0);
    const [isExportLoading, setIsExportLoading] = useState(false);
    const [isReportLoading, setIsReportLoading] = useState(false);
    const [runbookReport, setRunbookReport] = useState(null);

    const isLocked = runbook.status === RunbookStatus.InProgress;
    const isDone =
        runbook.status === RunbookStatus.Completed || runbook.status === RunbookStatus.Aborted;

    useEffect(() => {
        const sorted = [...(runbook.actions || [])].sort((a, b) => a.order - b.order);
        setActions(sorted);
    }, [runbook.actions]);

    // Clear search + filter when runbook starts or stops
    useEffect(() => {
        setSearch("");
        setStatusFilters(new Set());
    }, [runbook.status]);

    const filteredActions = actions.filter((a) => {
        const matchesSearch = !search.trim() || a.title.toLowerCase().includes(search.trim().toLowerCase());
        const matchesStatus = statusFilters.size === 0 || statusFilters.has(a.status);
        return matchesSearch && matchesStatus;
    });

    const toggleStatusFilter = (value) => {
        if (value === null) {
            setStatusFilters(new Set());
            return;
        }
        setStatusFilters((prev) => {
            const next = new Set(prev);
            if (next.has(value)) {
                next.delete(value);
            } else {
                next.add(value);
            }
            // All individual statuses selected → revert to All
            return next.size >= NON_ALL_STATUS_COUNT ? new Set() : next;
        });
    };

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id || isLocked) return;

        const oldIndex = actions.findIndex((a) => a.id === active.id);
        const newIndex = actions.findIndex((a) => a.id === over.id);


        dispatch(
            reorderRunbookActionRequest({
                runbookId: runbook.id,
                actionId: active.id,
                payload: {
                    newOrder: newIndex
                },
            })
        );
        return;
        const reordered = arrayMove(actions, oldIndex, newIndex).map((action, i) => ({
            ...action,
            order: i + 1,
        }));

        setActions(reordered); // optimistic update
        const changed = reordered.filter(
            (a, i) => a.order !== (runbook.actions || [])[i]?.order
        );

        try {
            changed.map((a) =>

                runbooksService.patchRunbookActionRequest({
                    runbookId: runbook.id,
                    actionId: a.id,
                    payload: [
                        {
                            op: "replace",
                            path: "/Order",
                            value: a.order,
                        },
                    ],
                })
            )
        }
        catch (error) {
            // Revert on error
            setActions([...(runbook.actions || [])].sort((a, b) => a.order - b.order));
            dispatch(generalActions.showToast({ title: "Something went wrong. Please try again later.", type: "error" }));
        }
    };

    const handleComplete = (action) => {
        dispatch(
            completeRunbookActionRequest({ runbookId: runbook.id, actionId: action.id })
        );
    };
    const handleDuplicate = (action) => {
        dispatch(
            duplicateRunbookActionRequest({ runbookId: runbook.id, actionId: action.id })
        );
    };

    const handleDelete = (action) => {
        dispatch(
            deleteRunbookActionRequest({ runbookId: runbook.id, actionId: action.id })
        );
    };

    const initRunbookExportConfig = () => {
        setExportFormat(0);
        setIsOpenPopover(true);
    }

    const handleRunbookExport = () => {
        setIsExportLoading(true);
        const exportParams = {
            projectId: projectId,
            format: exportFormat,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone
        };

        runbookService
            .getRunbookExportRequest(runbook.id, exportParams)
            .then((response) => {
                const contentDisposition = response.headers["content-disposition"];
                let fileName = `Runbook_${runbook.name}`;

                let fileExtension = "csv";

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?([^"]+)"?/);
                    if (match && match[1]) {
                        fileName = match[1].split(".")[0];
                    }
                }

                const fileType = response.headers.getContentType();
                if (fileType.includes("csv")) {
                    fileExtension = "csv";
                } else if (fileType.includes("spreadsheet")) {
                    fileExtension = "xlsx";
                }

                const blob = new Blob([response.data], { type: fileType });
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = `${fileName}.${fileExtension}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                dispatch(
                    generalActions.showToast({
                        title: "Export successful",
                        type: "success",
                    })
                );
            })
            .catch((error) => {
                dispatch(
                    generalActions.showToast({
                        title: "Export Failed. Please try again.",
                        type: "error",
                    })
                );
            })
            .finally(() => {
                setIsExportLoading(false);
                setIsOpenPopover(false);
            });
    }

    const handleRunbookReport = () => {
        setIsReportLoading(true);
        setRunbookReport(null);
        runbookService
            .getRunbookReportRequest({ id: runbook.id })
            .then((response) => {
                const pdfBlob = new Blob([response], { type: "application/pdf" });
                const blobUrl = URL.createObjectURL(pdfBlob);
                setRunbookReport({
                    show: true,
                    data: {
                        title: `${runbook.name} Report`,
                        pdfData: blobUrl
                    },
                });
            })
            .catch((error) => {
                dispatch(
                    generalActions.showToast({
                        title: "Report Failed. Please try again.",
                        type: "error",
                    })
                );
            })
            .finally(() => {
                setIsReportLoading(false);
            });
    }

    return (
        <div className={styles.actionsCard} style={{ maxHeight: `calc(100vh - 260px)` }}>
            <div className={styles.actionsHeader}>
                <div className={styles.actionsHeaderTop}>
                    <h6 className={styles.actionsTitle}>Actions</h6>
                    <span className={styles.actionsCount}>{actions.length}</span>
                </div>
                <div className={styles.actionsHeaderBottom}>
                    <div className={styles.actionsHeaderLeft}>
                        <div className={styles.actionsFilters}>
                            <input
                                className={styles.actionsSearch}
                                placeholder="Search actions…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <div className={styles.filterPills}>
                                {ACTION_FILTER_OPTIONS.map((opt) => {
                                    const isActive = opt.value === null
                                        ? statusFilters.size === 0
                                        : statusFilters.has(opt.value);
                                    return (
                                        <button
                                            key={opt.label}
                                            className={`${styles.filterPill} ${isActive ? styles.filterPillActive : ""}`}
                                            style={isActive ? { background: opt.color, borderColor: opt.color } : {}}
                                            onClick={() => toggleStatusFilter(opt.value)}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                    <div className={styles.actionsHeaderRight}>
                        <div className={""}>
                            <Button
                                label=""
                                icon={
                                    <div className="flex col gap-2 align-items-center justify-content-end">
                                        {isReportLoading ?
                                            (<>
                                                <Spinner
                                                    className="me-1"
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Loading...
                                            </>) :
                                            (<>
                                                <PrintIcon />
                                                Report
                                            </>)
                                        }
                                    </div>
                                }
                                clickHandler={() => handleRunbookReport()}
                                showLoading={isReportLoading}
                                btnClass={`${styles.reportButton}`}
                            />
                        </div>
                        <div>
                            <CustomPopover
                                icon={
                                    <Button
                                        btnClass={`d_btn ms-12 d_btn_primary ${styles.exportButton}`}
                                        icon={<FileDownSvg />}
                                        label="Export"
                                        clickHandler={() => initRunbookExportConfig()}
                                    />
                                }
                                minWidth={400}
                                PopSurfaceClass="mt-1"
                                status={openPopover}
                            >
                                {
                                    <div className="text-[#342c82] ">
                                        <div className="d-flex justify-content-end align-items-center mb-2">
                                            <div onClick={() => setIsOpenPopover(false)}>
                                                <Cross />
                                            </div>
                                        </div>
                                        <div className="d-flex flex-column">
                                            <div className={`${styles.exportFilterPopoverBody} FilterPopoverBody`}>
                                                <div className="FilterPopoverBodyScroll">
                                                    <div className="d-flex row align-items-center mb-3">
                                                        <>
                                                            <div className="col-2">Format</div>
                                                            <div className="col-4 align-items-center">
                                                                <DropDown
                                                                    dropDownwd="w-70"
                                                                    showLabel={false}
                                                                    options={exportFormats}
                                                                    defaultVal={getLabelByType(
                                                                        "exportFormat",
                                                                        exportFormat
                                                                    )}
                                                                    onChange={setExportFormat}
                                                                />
                                                            </div>
                                                        </>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-end gap-2 justify-content-end FilterPopoverFooter px-[10px]">
                                            <CustomButton
                                                btnClass="d_btn d_btn_white d_btn_whiteXsm"
                                                label="Cancel"
                                                icon={<X size={16} />}
                                                clickHandler={() => setIsOpenPopover(false)}
                                            />
                                            <CustomButton
                                                btnClass="d_btn d_btn_primary d_btn_primaryXsm"
                                                label="Export"
                                                icon={<Download size={16} />}
                                                clickHandler={() => {
                                                    handleRunbookExport();
                                                }}
                                                showLoading={isExportLoading}
                                            />
                                        </div>
                                    </div>
                                }
                            </CustomPopover>
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.actionsList}>
                {filteredActions.length === 0 && (
                    <div className={styles.emptyState}>
                        {actions.length === 0 ? "No actions yet. Add one below." : "No actions match your search."}
                    </div>
                )}

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={filteredActions.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                        {filteredActions.map((action) => (
                            <SortableActionRow
                                key={action.id}
                                action={action}
                                runbookId={runbook.id}
                                projectId={projectId}
                                isLocked={isLocked}
                                onComplete={handleComplete}
                                onDuplicate={handleDuplicate}
                                onDelete={() => handleDelete(action)}
                                onSave={onRefresh}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            </div>

            {/* Inline create form — pinned at bottom */}
            {!isDone && (
                <RunbookActionInlineForm
                    runbook={{ id: runbook.id, plannedStartDate: runbook.plannedStartDate }}
                    projectId={projectId}
                    existingActions={actions}
                    onSuccess={onRefresh}
                />
            )}

            {runbookReport && runbookReport?.show && (
                <Modal
                    title={`${runbookReport.data.title}`}
                    handleCancel={() => setRunbookReport(null)}
                    body={
                        runbookReport?.data?.pdfData && (
                            <iframe
                                title={`${runbookReport.data.title}`}
                                src={runbookReport?.data?.pdfData}
                                width="100%"
                                height="100%"
                            ></iframe>
                        )
                    }
                    type={true}
                    size=""
                />
            )}
        </div>
    );
};

// ─── RunbookOverview ─────────────────────────────────────────────────────────

const RunbookOverview = ({ runbookId, projectId }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const detail = useSelector(runbookDetail);
    const detailLoading = useSelector(runbookDetailLoading);
    const saving = useSelector(runbookLoading);
    const refresh = () => dispatch(getRunbookDetailRequest({ id: runbookId }));

    useEffect(() => {
        refresh();
    }, [runbookId]);

    const handleStart = () => dispatch(startRunbookRequest({ id: runbookId }));
    const handleStop = () => dispatch(stopRunbookRequest({ id: runbookId }));

    if (detailLoading && (!detail || detail.id !== runbookId)) {
        return (
            <div className={styles.page}>
                <Skeleton height={80} style={{ marginBottom: 12 }} />
                <Skeleton height={50} style={{ marginBottom: 12 }} />
                <Skeleton height={200} />
            </div>
        );
    }

    if (!detail) return null;

    return (
        <div className={styles.page}>
            <RunbookHeader
                runbook={detail}
                projectId={projectId || detail?.project?.id}
                onSave={refresh}
                onStart={handleStart}
                onStop={handleStop}
                saving={saving}
            />

            <RunbookActionsList
                runbook={detail}
                projectId={projectId || detail?.project?.id}
                onRefresh={refresh}
            />
        </div>
    );
};

export default RunbookOverview;
